import { pipe, Option } from 'effect';
import { AgDateFilterModel } from '@/components/AgGrid/components/AgDateFilter/types';
import { isSameJson } from '@/lib/effectHelpers';

type NormalizedCondition = { type: string; dateFrom: string | null; dateTo: string | null };

type NormalizedModel =
  | { kind: 'none' }
  | { kind: 'single'; condition: NormalizedCondition }
  | { kind: 'multi'; operator: 'and' | 'or'; conditions: NormalizedCondition[] };

const normalizeCondition = (condition: {
  type?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}): NormalizedCondition => ({
  type: condition.type ?? '',
  dateFrom: condition.dateFrom ?? null,
  dateTo: condition.dateTo ?? null,
});

/** The model's AND/OR operator, lower-cased; the filter submits it upper-cased. */
export const dateFilterModelOperator = (model: AgDateFilterModel): 'and' | 'or' =>
  pipe(
    Option.fromNullable(model),
    Option.flatMap((m) => Option.fromNullable('operator' in m ? m.operator : null)),
    Option.map((operator) => (operator.toLowerCase() === 'and' ? 'and' : 'or')),
    Option.getOrElse((): 'and' | 'or' => 'or'),
  );

const normalize = (model: AgDateFilterModel): NormalizedModel => {
  if (!model) return { kind: 'none' };
  if ('conditions' in model) {
    return {
      kind: 'multi',
      operator: dateFilterModelOperator(model),
      conditions: model.conditions.map(normalizeCondition),
    };
  }
  return { kind: 'single', condition: normalizeCondition(model) };
};

/**
 * Whether two date filter models filter the same rows. Missing and null dates are the same, and the operator is
 * compared without case: the grid stores what the filter submitted, presets may carry either form.
 */
export const isSameDateFilterModel = (a: AgDateFilterModel, b: AgDateFilterModel): boolean =>
  isSameJson(normalize(a), normalize(b));
